"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Pressable = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Pressable"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _Icon = _interopRequireDefault(require("../Icon"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
// function to get the dimentionals of the conditionals
const getPasswordDimensions = (password, width, height) => {
  return password ? {
    width,
    height
  } : {};
};
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);
const selectOuterStyle = (_ref, password) => {
  let {
    backgroundColor,
    outerBorderWidth,
    outerBorderColor,
    outerBorderGap,
    borderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    shadow,
    width,
    height
  } = _ref;
  return [{
    backgroundColor,
    ...(0, _ThemeProvider.applyShadowToken)(shadow),
    ...(0, _ThemeProvider.applyOuterBorder)({
      borderRadius,
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
      outerBorderWidth,
      outerBorderColor,
      outerBorderGap
    }),
    ...getPasswordDimensions(password, width, height),
    ..._Platform.default.select({
      web: {
        outline: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    })
  }, staticStyles.outer];
};
const calculatePadding = (padding, borderWidth) => padding && Math.max(0, padding - borderWidth); // Stable size as border changes

const selectInnerStyle = (_ref2, password) => {
  let {
    borderColor,
    borderWidth,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    borderRadius,
    padding = 0,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingBottom,
    width,
    height
  } = _ref2;
  return {
    // Inner borders animate with the icon and should be treated like a themable feature of the icon
    borderColor,
    borderRadius,
    borderWidth,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
    padding: calculatePadding(padding, borderWidth),
    paddingLeft: calculatePadding(paddingLeft, borderLeftWidth),
    paddingRight: calculatePadding(paddingRight, borderRightWidth),
    paddingTop: calculatePadding(paddingTop, borderTopWidth),
    paddingBottom: calculatePadding(paddingBottom, borderBottomWidth),
    ..._Platform.default.select({
      web: {
        pointerEvents: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    }),
    ...getPasswordDimensions(password, width, height)
  };
};

/**
 * A pressable themeless base component that handles pressable states and passes tokens
 * based on these to an outer border and a base Card component. Not intended to be used in
 * apps or sites directly: build themed components on top of this.
 */
const IconButton = /*#__PURE__*/_react.default.forwardRef((_ref3, ref) => {
  let {
    tokens,
    variant = {},
    icon: IconComponent,
    href,
    hrefAttrs,
    testID,
    accessibilityRole = href ? 'link' : 'button',
    ...rawRest
  } = _ref3;
  const {
    onPress,
    ...rest
  } = _utils.clickProps.toPressProps(rawRest);
  const selectedProps = selectProps({
    ...rest,
    accessibilityRole
  });
  const handlePress = () => {
    _utils.linkProps.handleHref({
      href,
      onPress
    })({
      nativeEvent: {
        target: ref?.current?.id
      }
    });
  };
  const getTokens = (0, _ThemeProvider.useThemeTokensCallback)('IconButton', tokens, variant);
  const getOuterStyle = pressableState => selectOuterStyle(getTokens((0, _utils.resolvePressableState)(pressableState), variant.password));
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Pressable.default, {
    ref: ref,
    href: href,
    onPress: handlePress,
    hrefAttrs: hrefAttrs,
    style: getOuterStyle,
    ...selectedProps,
    testID: testID,
    children: pressableState => {
      const themeTokens = getTokens((0, _utils.resolvePressableState)(pressableState));
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
        style: selectInnerStyle(themeTokens, variant.password),
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.default, {
          icon: IconComponent || themeTokens.icon,
          title: selectedProps.accessibilityLabel,
          tokens: (0, _utils.selectTokens)('Icon', themeTokens, 'icon'),
          variant: variant
        })
      });
    }
  });
});
IconButton.displayName = 'IconButton';
IconButton.propTypes = {
  ...selectedSystemPropTypes,
  variant: _utils.variantProp.propType,
  tokens: (0, _utils.getTokensPropType)('IconButton'),
  /**
   * A unique identifier for testing purposes.
   * Will be added as a `data-testid` attribute.
   */
  testID: _propTypes.default.string,
  /**
   * Defines the icon to be rendered
   */
  icon: _propTypes.default.elementType,
  /**
   * URL to navigate to when the `Iconbutton` is pressed
   */
  href: _propTypes.default.string,
  /**
   * URL options to navigate to when the `Iconbutton` is pressed
   */
  hrefAttrs: _propTypes.default.shape(_utils.hrefAttrsProp.types),
  /**
   * Function to execute when the `Iconbutton` is pressed
   */
  onPress: _propTypes.default.func
};
const staticStyles = _StyleSheet.default.create({
  outer: {
    alignSelf: 'baseline'
  }
});
var _default = exports.default = (0, _utils.withLinkRouter)(IconButton);